import { resolveChecklistMediaUri } from '@/utils/media/resolveChecklistMediaUri';
import { isStorageMediaReference } from '@/utils/media/partnerStorageReference';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useSelector } from '@legendapp/state/react';
import { databaseService } from '@/services/databaseService';
import { globalState } from '@/state/globalState';
import { historicalPhotoCacheKey, historicalPhotoCacheTable } from '@/services/sync/HistoricalPhotoCacheService';
import { isIOSChecklistMediaPreviewUri, resolveIOSChecklistMediaPreviewSource } from '@/utils/file/iosChecklistMediaSource';

const identityNow = () => JSON.stringify([
  globalState.user.user.get()?.id ?? '', globalState.user.profile.get()?.empresa_id ?? '',
  globalState.user.profile.get()?.id ?? '', globalState.user.isAuthenticated.get(),
  globalState.user.session.get()?.access_token ?? '',
]);

/** IDs locais e caches históricos viram src somente após leitura na identidade atual. */
export const useChecklistMediaPreview = (
  value: string | null | undefined,
  table: string,
  mimeType: string,
  scope?: { companyId: string; checklistId: string; fieldId?: string },
): string | null => {
  const identity = useSelector(identityNow);
  const [userId, companyId, profileId, authenticated] = JSON.parse(identity) as [string, string, string, boolean];
  const isPrivate = isStorageMediaReference(value);
  const isLocalWeb = Platform.OS === 'web' && /^checklist-(photos|signatures|marcacoes)\//.test(value ?? '');
  const isLocalIOS = Platform.OS === 'ios' && isIOSChecklistMediaPreviewUri(value);
  const isScopedPhoto = table === historicalPhotoCacheTable && Boolean(scope) && /^https?:\/\//.test(value ?? '');
  const authorized = Boolean(authenticated && userId && profileId === userId && scope?.companyId === companyId && scope.checklistId);
  const cacheId = isScopedPhoto && authorized && value
    ? historicalPhotoCacheKey({ userId, companyId, checklistId: scope!.checklistId }, value)
    : isLocalWeb ? value : null;
  const requestKey = `${identity}:${table}:${isPrivate || isLocalIOS ? value : cacheId ?? ''}:${scope?.companyId ?? ''}:${scope?.checklistId ?? ''}:${scope?.fieldId ?? ''}`;
  const [resolved, setResolved] = useState<{ key: string; uri: string | null } | null>(null);

  useEffect(() => {
    if (!isLocalIOS || !value || !authorized || !scope?.fieldId) return;
    let cancelled = false;
    setResolved(null);
    const currentScope = { companyId: scope.companyId, checklistId: scope.checklistId, fieldId: scope.fieldId };
    void resolveIOSChecklistMediaPreviewSource(value, currentScope).then(uri => {
      if (!cancelled && identityNow() === identity) setResolved({ key: requestKey, uri });
    }).catch(() => {
      // Falha de leitura conserva o valor original e seu tratamento de erro; nunca confirma perda do arquivo.
      if (!cancelled && identityNow() === identity) setResolved({ key: requestKey, uri: value });
    });
    return () => { cancelled = true; };
  }, [isLocalIOS, value, authorized, requestKey, identity, scope?.companyId, scope?.checklistId, scope?.fieldId]);

  useEffect(() => {
    if (!isPrivate || !value) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      setResolved(null);
      try {
        const uri = await resolveChecklistMediaUri(value, scope);
        if (!cancelled && identityNow() === identity) {
          setResolved({ key: requestKey, uri });
          timer = setTimeout(() => { void load(); }, 270000);
        }
      } catch { if (!cancelled) setResolved(null); }
    };
    void load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isPrivate, value, requestKey, identity, scope?.companyId, scope?.checklistId]);

  useEffect(() => {
    if (!cacheId) return;
    let cancelled = false;
    setResolved(null);
    void databaseService.readItem<{ base64?: string }>(table, cacheId).then(row => {
      if (!cancelled && identityNow() === identity) {
        setResolved(row?.base64 ? { key: requestKey, uri: `data:${mimeType};base64,${row.base64}` } : null);
      }
    }).catch(() => { if (!cancelled) setResolved(null); });
    return () => { cancelled = true; };
  }, [cacheId, requestKey, identity, table, mimeType]);

  if (isPrivate) return resolved?.key === requestKey ? resolved.uri : null;
  if (isLocalIOS) return authorized && scope?.fieldId && resolved?.key === requestKey ? resolved.uri : null;
  if (isScopedPhoto && !authorized) return null;
  if (cacheId && resolved?.key === requestKey) return resolved.uri;
  if (isLocalWeb) return null;
  return value || null;
};
